import type { FC, PropsWithChildren } from 'react';
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { useLinkClassNames } from '../../util/useLinkClassNames';

import type { InternalLinkProps } from './InternalLink.types';
import { useToPropWithState } from './hooks';

const InternalLink: FC<PropsWithChildren<InternalLinkProps>> = ({
  to,
  onClick,
  children,
  ariaLabel,
  title,
  disallowTabFocus,
  ariaCurrent,
  onHover,
  onFocus,
  role,
  focusableRef,
  replace,
  ...restProps
}) => {
  const tabIndex = disallowTabFocus ? -1 : undefined;
  const ariaCurrentValue = ariaCurrent ? 'page' : undefined;
  const linkRef = useRef<HTMLAnchorElement>(null);
  const className = useLinkClassNames(restProps);
  const toWithState = useToPropWithState(to);

  useEffect(() => {
    const focus = () => {
      linkRef.current?.focus();
    };

    focusableRef?.({ focus });
  }, [focusableRef]);

  return (
    <Link
      to={toWithState}
      tabIndex={tabIndex}
      onClick={onClick}
      aria-label={ariaLabel}
      className={className.link}
      title={title}
      aria-current={ariaCurrentValue}
      onMouseEnter={onHover}
      onFocus={onFocus}
      role={role}
      innerRef={linkRef}
      replace={replace}
    >
      {children}
    </Link>
  );
};

export default InternalLink;
